import React, {
    useCallback,
    useMemo,
    useState,
    useEffect,
    forwardRef,
    MutableRefObject,
    ForwardedRef,
    ChangeEvent
} from 'react';
import {Input, TabMenuItem, TabsMenu, TabsSkinEnum} from '@priority-software/priority-style-react';
import {TabsDataItem} from '../constants';
import {SearchableList} from '../../../../../components/SearchableList';
import cx from 'classnames';
import {texts} from '../../../texts';
import {AutomationChoose} from '../../../reducer/model';
import styles from '../styles.module.scss';
import {priority} from '@priority-software/priority-proto-api/lib';
import {isRTL} from '../../../../../Localization';

type Props = {
    optionsData: TabsDataItem[];
    value: string;
    placeHolderText?: string;
    direction?: 'ltr' | 'rtl' | 'auto';
    onValueChange: (value: string) => void;
    onInputValueChange: (value: string) => void;
};

export const FunctionDialogContent = forwardRef(
    (props: Props, ref: ForwardedRef<HTMLInputElement>) => {
        const inputRef = ref as MutableRefObject<HTMLInputElement>;

        const {
            value,
            optionsData,
            placeHolderText = texts.function_dialog_input_placeholder,
            direction = 'ltr',
            onValueChange,
            onInputValueChange
        } = props;
        const [data] = useState<Array<TabsDataItem>>(optionsData);
        const [activeTabId, setActiveTabId] = useState((data[0] as TabsDataItem).id);

        const activeData = useMemo(() => {
            return data.find(tab => tab.id === activeTabId)?.data || [];
        }, [activeTabId, data]);

        const handleValue = useCallback(
            (e: ChangeEvent<HTMLInputElement>) => {
                onInputValueChange(e.currentTarget.value);
            },
            [onInputValueChange]
        );

        const handleTabChange = useCallback((id: string) => {
            setActiveTabId(id);
        }, []);

        const onItemClick = useCallback(
            (option: AutomationChoose) => {
                let value = option.label;

                if (activeTabId === 'fields') {
                    const optionData = option.data as priority.netitems.form.IAlertChoose;
                    value = optionData.Title?.includes('<<')
                        ? optionData.Title
                        : `<<${optionData.Title}>>`;
                }
                onValueChange(value);
            },
            [onValueChange, activeTabId]
        );

        useEffect(() => {
            requestAnimationFrame(() => {
                (inputRef.current as HTMLInputElement)?.focus();
            });
        }, []);

        return (
            <div>
                <Input
                    componentClasses={cx({
                        [styles.input]: direction === 'ltr',
                        [styles.rtlInput]: isRTL()
                    })}
                    direction={direction}
                    inputRef={inputRef}
                    size="large"
                    value={value}
                    onChange={handleValue}
                    placeholder={placeHolderText}
                />

                <div className={styles.tabsWrap}>
                    <TabsMenu skin={TabsSkinEnum.folder} bottomBorder={false}>
                        {data.map(tab => (
                            <TabMenuItem
                                key={tab.id}
                                onTabClick={handleTabChange}
                                isActive={activeTabId === tab.id}
                                text={tab.title}
                                id={tab.id}
                            />
                        ))}
                    </TabsMenu>
                </div>

                <SearchableList
                    componentClasses={styles.listWrap}
                    data={activeData}
                    onItemClick={onItemClick}
                    key={activeTabId}
                />
            </div>
        );
    }
);
